import dayjs from 'dayjs';
import _ from 'lodash';
import { convertNumberStringIntoNumber, formatNumWithKMBAlt, getPercentageChangeV2 } from 'utils/helperMethods';

var globalCounter = -1;

export const simulationOutputDataParser = (simulationRawData: any, outputKeyMapping: any) => {
    const newData = simulationRawData.map((item: any) => JSON.parse(item));

    const output: Array<any> = [];
    globalCounter = -1;
    buildSimulationOutputDataParser({ children: newData }, globalCounter, output, null, outputKeyMapping);
    return output;
};

function buildSimulationOutputDataParser(
    item: any,
    index: number,
    output: Array<any>,
    parentIndex: number | null,
    outputKeyMapping: any,
) {
    const keymapping: any = outputKeyMapping;

    if (item?.name) {
        const temp: any = {};
        item?.kpis?.forEach((k: any) => {
            temp[keymapping[k.name]?.label] = {
                pre: keymapping[k.name].pre,
                post: keymapping[k.name].pos,
                change: k.change,
                val: k.value,
            };
        });

        const row = {
            label: item.name,
            level: item.level,
            index: index,
            nestedIndex: item.level === 'product' ? null : parentIndex,
            ...temp,
        };
        output.push(row);
    }

    if (item?.children?.length) {
        item?.children?.forEach((child: any) => {
            globalCounter += 1;
            buildSimulationOutputDataParser(child, globalCounter, output, index, outputKeyMapping);
        });
    }
}

export const simulationInputDataParser = (
    simulationRawData: any,
    levels: Array<any>,
    inputKeyMapping: any,
    isCreateMode: boolean = false,
) => {
    const simulationInputData = simulationRawData.map((input: any) => JSON.parse(input));
    const simulationInputParsedData: Array<any> = [];

    globalCounter = -1;
    buildSimulationInputDataParser(
        { children: simulationInputData },
        globalCounter,
        simulationInputParsedData,
        null,
        levels,
        inputKeyMapping,
        isCreateMode,
    );
    return simulationInputParsedData;
};

function buildSimulationInputDataParser(
    item: any,
    index: number,
    output: Array<any>,
    parentIndex: number | null,
    levels: Array<any>,
    inputKeyMapping: any,
    isCreateMode: boolean = false,
) {
    const keymapping: any = inputKeyMapping;
    if (item?.name) {
        const tempNested: any = {
            cp: {
                pre: '',
                post: '',
                val: '',
            },
            np: {
                pre: '',
                post: '%',
                rval: '',
                val: '',
            },
            pc: {
                pre: '',
                post: '%',
                rval: '',
                val: '',
            },
        };

        const temp: any = {
            'Comp Follow': {
                nested: {
                    cp: { val: false }, // we will maintain true / false in cp variable
                    np: { val: null },
                    pc: { val: null },
                },
            },
        };

        Object.keys(item?.kpis || {}).forEach((k) => {
            tempNested.cp.val = item.kpis[k].cp;
            tempNested.np.rval =
                isNaN(item.kpis[k].np) || _.isNil(item.kpis[k].np) ? null : _.round(item.kpis[k].np, 2);
            tempNested.pc.rval = item.kpis[k].pc;
            if (!isCreateMode) {
                tempNested.np.val = item.kpis[k].np || '';
                tempNested.pc.val = item.kpis[k].pc;
            }
            temp[keymapping[k]] = {
                nested: JSON.parse(JSON.stringify(tempNested)),
            };
        });
        const row = {
            label: item.name,
            level: item.level,
            index: index,
            nestedIndex: item.level === levels[0] ? null : parentIndex,
            isExpandable: item.level === levels[0] ? true : false,
            ...temp,
        };
        output.push(row);
    }

    if (item?.children?.length) {
        item?.children?.forEach((child: any) => {
            globalCounter += 1;
            buildSimulationInputDataParser(child, globalCounter, output, index, levels, inputKeyMapping, isCreateMode);
        });
    }
}

// helper method
const getIndividualItem = (item: any, kpiMapping: any) => {
    const kpiObj: any = {};

    Object.keys(kpiMapping).forEach((kpi) => {
        kpiObj[kpiMapping[kpi]] = {
            cp: item[kpi].nested.cp.val ?? null,
            np: item[kpi].nested.np?.val || item[kpi].nested.np?.rval || null,
            // new value will either be value user entered or recommended value , calculate pc w.r.t new and old price
            pc: getPercentageChangeV2(item[kpi].nested.cp.val, item[kpi].nested.np?.val || item[kpi].nested.np?.rval),
        };
    });

    const result = { name: item.label, level: item.level, kpis: kpiObj };
    return result;
};

export const processInputVariablesForOutputCal = (data: any, levels: Array<string>, kpisMapping: any) => {
    const outputPpgVariables: any = [];

    let level1Childrens: any = [];
    let level2Childrens: any = [];

    // for computation purpose reversing the entire array
    let newData = data.reverse();
    newData.forEach((item: any) => {
        switch (item.level) {
            case levels[0]:
                const level0Obj: any = getIndividualItem(item, kpisMapping);
                // to keep the retailers in the same order we had to reverse()
                level0Obj.children = [...level1Childrens.reverse()];
                level1Childrens = [];
                outputPpgVariables.push(level0Obj);
                break;

            case levels[1]:
                const level1Obj: any = getIndividualItem(item, kpisMapping);
                // maintain the same order for distributors
                level1Obj.children = [...level2Childrens.reverse()];
                level2Childrens = [];
                level1Childrens.push(level1Obj);
                break;
            case levels[2]:
                level2Childrens.push(getIndividualItem(item, kpisMapping));
                break;

            default:
                break;
        }
    });

    // after computation reversing the array back to same
    newData = outputPpgVariables.reverse();

    // converting all the number string into number to avoid gql issue
    convertNumberStringIntoNumber(newData);

    const stringifiedOutput: any = [];
    newData.forEach((item: any) => {
        stringifiedOutput.push(JSON.stringify(item));
    });

    return stringifiedOutput;
};

export const fetchAllScenariosParser = (data: any[], dataSource: Array<any>) => {
    try {
        const result = data?.map((item: any) => {
            let kpiObject: any = {};
            item?.kpis?.forEach((kpi: any) => {
                kpiObject[kpi?.kpiName] = _.isNil(kpi?.kpiValue) ? '' : formatNumWithKMBAlt(kpi?.kpiValue, 2, 2);
            });
            return {
                key: item.scenarioId,
                Scenario: item?.scenarioName,
                scenarioId: item?.scenarioId,
                'User Name': item?.userName,
                'Data Source':
                    item?.dataSourceType && dataSource.find((d: any) => d.value === item?.dataSourceType)?.label,
                Date: dayjs(item?.modifiedOn).format('MM/DD/YYYY'),
                ...kpiObject,
            };
        });
        return result;
    } catch (error) {
        throw error;
    }
};

export const fetchPortfolioRecommendationMetricsParser = (data: any[]) => {
    try {
        let recomObj: any = {};
        data.forEach((item: any) => {
            recomObj[item.kpiName] = item.kpiValue;
        });
        return recomObj;
    } catch (error) {
        throw error;
    }
};

export const simulationKpiParser = (data: any[], kpiMetrictsState: any[], parseJson: boolean = false) => {
    try {
        if (parseJson) {
            data = data.map((i: any) => JSON.parse(i));
        }

        const result = kpiMetrictsState?.map((item: any, index: number) => {
            const val = data?.find(
                (responseItem: any) => item.value === responseItem.kpiName || item.value === responseItem.kpi_name,
            );

            return {
                ...item,
                simulatedValue: parseJson ? val?.kpi_value ?? '' : val?.kpiValue ?? '',
            };
        });

        return result;
    } catch (error) {
        throw error;
    }
};

export const portfolioUpdateKpiParser = (data: any) => {
    const result: any = [];
    data?.forEach((item: any, index: number) => {
        const newItem: any = {
            label: item.rowAttributeName,
            key: index + 1,
        };
        const kpiList: any = {};
        // processing every retailer column
        item?.columnData?.forEach((ret: any) => {
            const valueSales = ret.kpisData.find((k: any) => k.kpiName === 'value').kpiValue;
            // in edit mode additional data will be present use that
            newItem[ret.colAttributeName] = {
                value: valueSales,
                isActive: ret?.additionalData ? ret?.additionalData?.isActive : true,
                inActivationDate: ret?.additionalData?.inActivationDate || [null],
                inActivationDateEpochTime: ret?.additionalData?.inActivationDateEpochTime || [null],
                inputList: ret?.additionalData?.inputList || {},
            };

            const kpiObj: any = {};
            ret.kpisData.forEach((i: any) => {
                kpiObj[i.kpiName] = i.kpiValue;
            });
            kpiList[ret.colAttributeName] = kpiObj;
        });
        newItem.kpiList = kpiList;
        result.push(newItem);
    });

    return result;
};

export const reversePortfolioUpdateKpiParser = (data: any) => {
    const result: any = [];
    data?.forEach((item: any, index: number) => {
        const newItem: any = { rowAttributeName: item?.label };
        const columnData: any = [];

        Object.keys(item || {})
            .filter((col: string) => !['label', 'kpiList', 'key'].includes(col))
            .forEach((key: any) => {
                const currentObj: any = item[key];
                const columnItem: any = {
                    colAttributeName: key,
                    additionalData: {
                        inputList: currentObj.inputList,
                        isActive: currentObj.isActive,
                        inActivationDate: currentObj.inActivationDate,
                        inActivationDateEpochTime: currentObj.inActivationDateEpochTime,
                    },
                };
                const kpisData: any = [];
                Object.keys(item.kpiList[key]).forEach((kpiKey: string) => {
                    kpisData.push({
                        kpiName: kpiKey,
                        kpiValue: item.kpiList[key][kpiKey],
                    });
                });
                columnItem.kpisData = kpisData;
                columnData.push(columnItem);
            });

        newItem.columnData = columnData;

        result.push(newItem);
    });

    return result?.map((item: any) => JSON.stringify(item));
};

export const quartersMetricsDataParser = (data: any, quarterMetricKeyMapping: any) => {
    const result: any = [];

    quarterMetricKeyMapping.forEach((configKey: any) => {
        const rowItem: any = { "KPI's": configKey.name };
        data.forEach((d: any, index: number) => {
            const key = 'Q' + (index + 1);
            rowItem[key] = {
                pre: configKey.pre,
                post: configKey.post,
                value: d[configKey.value],
            };
        });
        result.push(rowItem);
    });

    return result;
};
